import { MessagingSdk } from 'lib-klab-sdks';
import {
  ChatContactUI,
  ChatGroupUI,
  ChatMessageUI,
  ChatConversationUI,
} from '../../../domain/models/messaging/chat-ui.model';
import { colorForUserId, shortNameFromGroupName } from './chat-color.util';
import { formatChatDisplayName } from './chat-display-name.util';
import { formatChatTime, formatLastSeen } from './time-format.util';

type Snapshot = MessagingSdk.ConversationSnapshot;
type Participant = MessagingSdk.ParticipantSnapshot;
type Last = MessagingSdk.LastMessageSnapshot;
type Pinned = MessagingSdk.PinnedMessageSnapshot;

/**
 * Maps a backend {@link Snapshot} into the UI's split representation:
 *   - a {@link ChatGroupUI} when {@code type === 'GROUP'}
 *   - a {@link ChatContactUI} when {@code type === 'DIRECT'} (the "other" participant)
 *
 * <p>{@code currentUserId} is used to skip the current user when picking
 * the counterpart of a DIRECT conversation.
 */
export function mapSnapshotToGroupOrContact(
  snap: Snapshot,
  currentUserId: number,
): { group?: ChatGroupUI; contact?: ChatContactUI } {
  if (snap.type === 'GROUP') {
    return { group: mapGroupSnapshot(snap, currentUserId) };
  }
  return { contact: mapDirectSnapshotAsContact(snap, currentUserId) };
}

export function mapGroupSnapshot(snap: Snapshot, currentUserId?: number): ChatGroupUI {
  const name = snap.groupInfo?.name ?? 'Grupo';
  const activeMembers = (snap.participants ?? []).filter((p) => p.isActive).length;
  const currentParticipant = (snap.participants ?? []).find(
    (p) =>
      p.userId === currentUserId &&
      p.isActive === true,
  );
  const canManage = currentParticipant?.role === 'ADMIN' || currentParticipant?.role === 'MODERATOR';
  const canUpdate = currentParticipant?.role === 'ADMIN';
  const canDelete = currentParticipant?.role === 'ADMIN';
  const canDeleteMessages =
    currentParticipant?.role === 'ADMIN' || currentParticipant?.role === 'MODERATOR';
  const onlyAdminsCanSend = snap.groupInfo?.onlyAdminsCanSend === true;
  const canSendMessages =
    !onlyAdminsCanSend ||
    currentParticipant?.role === 'ADMIN' ||
    currentParticipant?.role === 'MODERATOR';

  return {
    id: snap.conversationId,
    name,
    shortName: shortNameFromGroupName(name),
    bgColor: colorForUserId(snap.conversationId),
    textColor: '#ffffff',
    iconUrl: snap.groupInfo?.avatarS3Key ?? undefined,
    unreadCount: snap.unreadCount > 0 ? snap.unreadCount : undefined,
    memberCount: activeMembers || undefined,
    lastActivityAt: snapshotActivityTime(snap),
    canManage,
    canUpdate,
    canDelete,
    canDeleteMessages,
    canDesignate: canManage,
    isPinned: !!currentParticipant?.pinnedAt,
    onlyAdminsCanSend,
    canSendMessages,
  };
}

export function mapDirectSnapshotAsContact(
  snap: Snapshot,
  currentUserId: number,
): ChatContactUI {
  const others = (snap.participants ?? []).filter((p) => p.userId !== currentUserId);
  const other: Participant | undefined = others[0] ?? snap.participants?.[0];
  const self = (snap.participants ?? []).find((p) => p.userId === currentUserId);

  return {
    id: snap.conversationId,
    userId: other?.userId ?? null,
    employeeId: other?.employeeId ?? null,
    name: formatChatDisplayName({ displayName: other?.displayName, fullName: other?.fullName, fallback: 'Usuario' }),
    searchText: other?.fullName,
    role: other?.roleName ?? '',
    avatarUrl: other?.photoUrl ?? undefined,
    color: colorForUserId(other?.userId ?? snap.conversationId),
    isConnected: other?.isOnline ?? false,
    unreadCount: snap.unreadCount > 0 ? snap.unreadCount : undefined,
    lastSeen: other?.isOnline ? undefined : formatLastSeen(other?.lastSeen),
    lastActivityAt: snapshotActivityTime(snap),
    lastMessageAt: snap.lastMessage?.sentAt ? Date.parse(snap.lastMessage.sentAt) : undefined,
    lastMessagePreview: buildContactLastMessagePreview(snap.lastMessage),
    isDeleted: !!other?.confirmedDeletedAt,
    isPinned: !!self?.pinnedAt,
  };
}

/**
 * Maps the participants of a GROUP conversation to {@link ChatContactUI}s.
 * Inactive participants are filtered out. The current user is also skipped.
 */
export function mapParticipantsToContacts(
  snap: Snapshot,
  currentUserId: number,
): ChatContactUI[] {
  return (snap.participants ?? [])
    .filter((p) => p.isActive && p.userId !== currentUserId)
    .map((p) => participantToContact(p));
}

function participantToContact(p: Participant): ChatContactUI {
  return {
    id: String(p.userId),
    userId: p.userId,
    employeeId: p.employeeId,
    name: formatChatDisplayName({ displayName: p.displayName, fullName: p.fullName, fallback: 'Usuario' }),
    searchText: p.fullName,
    role: p.roleName,
    participantRole: p.role,
    isAssignedAdmin: p.role === 'MODERATOR',
    avatarUrl: p.photoUrl ?? undefined,
    color: colorForUserId(p.userId),
    isConnected: p.isOnline,
    lastSeen: p.isOnline ? undefined : formatLastSeen(p.lastSeen),
  };
}

/**
 * Builds a full {@link ChatConversationUI} from a snapshot.
 * Starts with no messages — those arrive via {@code chat:message:new} or
 * a separate history endpoint (not yet defined).
 */
export function mapSnapshotToConversation(
  snap: Snapshot,
  currentUserId: number,
): ChatConversationUI {
  const isGroup = snap.type === 'GROUP';

  if (isGroup) {
    return {
      id: snap.conversationId,
      visibilityEpoch: snap.visibility?.epoch ?? null,
      visibilityGeneration: snap.visibility?.generation ?? null,
      visibleAfterSequence: snap.visibility?.visibleAfterSequence ?? null,
      type: 'group',
      group: mapGroupSnapshot(snap, currentUserId),
      messages: lastMessageAsList(snap.lastMessage, currentUserId),
      pinnedMessages: pinnedMessagesAsList(snap.pinnedMessages, currentUserId),
    };
  }

  return {
    id: snap.conversationId,
    visibilityEpoch: snap.visibility?.epoch ?? null,
    visibilityGeneration: snap.visibility?.generation ?? null,
    visibleAfterSequence: snap.visibility?.visibleAfterSequence ?? null,
    type: 'individual',
    contact: mapDirectSnapshotAsContact(snap, currentUserId),
    messages: lastMessageAsList(snap.lastMessage, currentUserId),
    pinnedMessages: pinnedMessagesAsList(snap.pinnedMessages, currentUserId),
  };
}

export function pinnedMessagesAsList(
  pinnedMessages: Pinned[] | null | undefined,
  currentUserId: number,
): ChatMessageUI[] {
  return (pinnedMessages ?? []).map((pinned) => ({
    id: pinned.messageId,
    senderId: String(pinned.senderId ?? ''),
    senderName: formatChatDisplayName({ senderName: pinned.senderName }),
    content: pinned.contentPreview ?? '',
    type: pinned.type,
    timestamp: formatChatTime(pinned.sentAt),
    status: 'sent',
    isOwn: pinned.senderId === currentUserId,
    isPinned: true,
    reactions: [],
    reactionsRevision: 0,
    serverSentAt: pinned.sentAt,
  }));
}

function lastMessageAsList(
  last: Last | null | undefined,
  currentUserId: number,
): ChatMessageUI[] {
  if (!last) return [];
  return [
    {
      id: last.messageId ?? '',
      sequence: Number.isFinite(last.sequence) ? last.sequence! : undefined,
      senderId: String(last.senderId ?? ''),
      content: last.contentPreview ?? '',
      type: last.type,
      timestamp: formatChatTime(last.sentAt),
      status: last.status ?? 'sent',
      isOwn: last.senderId === currentUserId,
      reactions: [],
      reactionsRevision: 0,
      serverSentAt: last.sentAt,
    },
  ];
}

/**
 * Preview de "último mensaje" estilo WhatsApp para la lista de contactos del
 * dashboard. `LastMessageSnapshot` no trae `fileName`, así que el fallback
 * por tipo no puede replicar el nombre real del archivo (eso solo lo tiene
 * el camino en vivo de ChatMessageUI) — degradación menor y esperable.
 */
function buildContactLastMessagePreview(last: Last | null | undefined): string | undefined {
  if (!last) return undefined;
  if (last.contentPreview) return last.contentPreview;
  if (last.type === 'IMAGE') return 'Imagen';
  if (last.type === 'FILE') return 'Archivo adjunto';
  return last.contentPreview ?? '';
}

function snapshotActivityTime(snap: Snapshot): number | undefined {
  const raw = snap.lastMessage?.sentAt ?? snap.updatedAt;
  const parsed = raw ? Date.parse(raw) : Number.NaN;
  return Number.isFinite(parsed) ? parsed : undefined;
}
